import { defineStore } from 'pinia'
import { coursesData as defaultCourses } from '../data/coursesData'
import { fetchCoursesApi, getCourseDetailApi, deleteCourseApi, updateCourseApi, createCourseApi } from '../services/api'

const defaultSponsorsList = [
  { id: 'sp-1', name: 'Fundinho Mercearia', logo: '/images/coworking/Fundinho.webp', link: 'https://instagram.com/fundinho.mercearia' },
  { id: 'sp-2', name: 'Schluck', logo: '/images/coworking/Schluck.webp', link: 'https://www.instagram.com/schluckbr' },
  { id: 'sp-3', name: 'Filippini', logo: '/images/coworking/Filippini.webp', link: 'https://www.instagram.com/cachacafilippini' },
  { id: 'sp-5', name: 'Fabbri 1905', logo: '/images/coworking/Fabbri.webp', link: 'https://www.instagram.com/fabbri1905brasil' },
  { id: 'sp-6', name: 'Preshh', logo: '/images/coworking/Preshh.webp', link: 'https://www.instagram.com/preshhoficial' },
  { id: 'sp-7', name: 'Ice Prime', logo: '/images/coworking/IcePrime.webp', link: 'https://www.instagram.com/iceprime.oficial' }
]

export const useCoursesStore = defineStore('courses', {
  state: () => ({
    courses: [],
    isEditMode: false,
    activeEditForm: {
      slug: ''
    },
    saveTriggerCount: 0,
    sponsorModalTriggerCount: 0,
    isLoading: false,
  }),

  getters: {
    allCourses: (state) => state.courses,
    publicCourses: (state) => state.courses.filter(c => c.published !== false),
    openCourses: (state) => state.courses.filter(c => c.status === 'open' && c.published !== false),
    closedCourses: (state) => state.courses.filter(c => c.status === 'closed' && c.published !== false),
    
    getCourseBySlug: (state) => (slug) => {
      return state.courses.find(c => c.slug === slug)
    },

    isSlugAvailable: (state) => (slug, currentSlug = '') => {
      if (!slug) return false
      const clean = slug.toLowerCase().trim().replace(/[^a-z0-9-]+/g, '-')
      return !state.courses.some(c => c.slug === clean && c.slug !== currentSlug)
    },

    allLeads: (state) => {
      const leads = []
      state.courses.forEach(course => {
        (course.turmas || []).forEach(turma => {
          (turma.students || []).forEach(student => {
            leads.push({
              ...student,
              courseSlug: course.slug,
              courseTitle: course.title,
              turmaId: turma.id,
              turmaName: turma.name,
              turmaDate: turma.dateText
            })
          })
        })
      })
      return leads.sort((a, b) => new Date(b.registeredAt || 0) - new Date(a.registeredAt || 0))
    },

    getTurmaSeats: (state) => (courseSlug, turmaId) => {
      const course = state.courses.find(c => c.slug === courseSlug)
      if (!course) return { maxSeats: 20, takenSeats: 0, waitlistSeats: 0, remainingSeats: 20 }
      const turma = (course.turmas || []).find(t => t.id === turmaId) || (course.turmas || [])[0]
      if (!turma) return { maxSeats: 20, takenSeats: 0, waitlistSeats: 0, remainingSeats: 20 }
      
      const max = turma.maxSeats || 20
      const confirmed = (turma.students || []).filter(s => s.status === 'confirmed' || s.status === 'completed').length
      const waitlist = (turma.students || []).filter(s => s.status === 'waitlist' || s.status === 'pending').length
      const remaining = Math.max(0, max - confirmed)
      return { maxSeats: max, takenSeats: confirmed, waitlistSeats: waitlist, remainingSeats: remaining }
    }
  },

  actions: {
    async initStore() {
      if (this.isLoading) return
      this.isLoading = true
      try {
        const res = await fetchCoursesApi()
        if (res.data && res.data.courses) {
          this.courses = res.data.courses
        } else {
          this.courses = []
        }
      } catch (e) {
        console.error('Falha ao carregar cursos:', e)
      } finally {
        this.isLoading = false
      }
    },

    async fetchCourseBySlug(slug, isPreview = false) {
      if (!slug) return null
      try {
        const res = await getCourseDetailApi(slug, isPreview ? { preview: 1 } : {})
        if (res.data && res.data.course) {
          const idx = this.courses.findIndex(c => c.slug === slug)
          if (idx !== -1) {
            this.courses[idx] = res.data.course
          } else {
            this.courses.push(res.data.course)
          }
          return res.data.course
        }
      } catch (err) {
        console.warn(`Curso "${slug}" não encontrado ou sem acesso de preview:`, err)
        return null
      }
    },

    async createCourse(payload) {
      let rawSlug = payload.slug
        ? payload.slug.toLowerCase().trim().replace(/[^a-z0-9-]+/g, '-')
        : (payload.title ? payload.title.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'novo-curso')

      if (!rawSlug) rawSlug = 'novo-curso'

      let cleanSlug = rawSlug
      let counter = 1
      while (this.courses.some(c => c.slug === cleanSlug)) {
        cleanSlug = `${rawSlug}-${counter}`
        counter++
      }

      const newCourse = {
        slug: cleanSlug,
        title: payload.title || 'Novo Curso',
        priceText: payload.priceText || 'R$ 1.500,00',
        published: payload.published !== undefined ? payload.published : true,
        bgListing: payload.bgListing || '/images/events/fundocursobasico.webp',
        bgDetail: payload.bgDetail || '/images/events/fundocursobasicoblurdark.webp',
        status: payload.status || 'open',
        format: payload.format || 'Curso Presencial com Certificado',
        cityState: payload.cityState || 'Joinville/SC',
        address: payload.address || 'Espaço Bar Experts',
        dateText: payload.dateText || 'Data a confirmar',
        countdownDate: payload.countdownDate || '',
        teachers: payload.teachers || [{ name: 'Equipe Bar Experts', link: 'https://www.instagram.com/barexperts' }],
        scheduleHours: payload.scheduleHours || '12:00 às 18:00',
        description: Array.isArray(payload.description) ? payload.description : [payload.description || 'Descrição do curso.'],
        inclusionsLeft: payload.inclusionsLeft || ['Aulas Teóricas', 'Aulas Práticas'],
        inclusionsRight: payload.inclusionsRight || ['Material Digital', 'Certificado'],
        program: payload.program || [],
        sponsors: payload.sponsors || [...defaultSponsorsList],
        collaborator_ids: payload.collaborator_ids || [],
        turmas: [
          {
            id: `turma-${cleanSlug}-01`,
            name: 'Turma 01',
            dateText: payload.dateText || 'Em breve',
            cityState: payload.cityState || 'Joinville/SC',
            address: payload.address || 'Espaço Bar Experts',
            scheduleHours: payload.scheduleHours || '12:00 às 18:00',
            maxSeats: payload.maxSeats || 20,
            status: 'open',
            students: []
          }
        ]
      }

      await createCourseApi(newCourse)
      await this.initStore() // refresh
      return this.courses.find(c => c.slug === cleanSlug)
    },

    async togglePublishCourse(slug) {
      const course = this.courses.find(c => c.slug === slug)
      if (!course) throw new Error('Curso não encontrado.')
      
      const newPublished = !course.published
      await updateCourseApi(slug, { published: newPublished })
      course.published = newPublished
      return course.published
    },

    canUserEditCourse(slug, userRoleLevel) {
      const course = this.courses.find(c => c.slug === slug)
      if (!course) return false
      // Se for rascunho interno, nível >= 1 já consegue editar se for autor ou convidado (mas a UI/Store confia na API)
      if (course.published === false) {
        return userRoleLevel >= 1
      }
      // Se for publicado, exige Nível 4 ou 5
      return userRoleLevel >= 4
    },

    async updateCourse(oldSlug, updatedFields) {
      const course = this.courses.find(c => c.slug === oldSlug || (updatedFields.id && c.id === updatedFields.id))
      if (!course) throw new Error('Curso não encontrado.')

      if (updatedFields.slug && updatedFields.slug !== course.slug) {
        const cleanNewSlug = updatedFields.slug.toLowerCase().trim().replace(/[^a-z0-9-]+/g, '-')
        if (!this.isSlugAvailable(cleanNewSlug, course.slug)) {
          throw new Error(`O slug "${cleanNewSlug}" já está em uso por outro curso.`)
        }
      }

      await updateCourseApi(oldSlug, updatedFields)
      await this.initStore()
      return this.courses.find(c => c.slug === (updatedFields.slug || oldSlug))
    },

    async addSponsorToCourse(courseSlug, sponsorPayload) {
      const course = this.courses.find(c => c.slug === courseSlug)
      if (!course) throw new Error('Curso não encontrado.')
      if (!course.sponsors) course.sponsors = [...defaultSponsorsList]

      const newSponsor = {
        id: `sp-${Date.now()}`,
        name: sponsorPayload.name || 'Novo Patrocinador',
        logo: sponsorPayload.logo, 
        link: sponsorPayload.link || 'https://www.instagram.com/barexperts',
        ratio: sponsorPayload.ratio || '1:1'
      }

      course.sponsors.push(newSponsor)
      await updateCourseApi(course.slug, { sponsors: course.sponsors })
      return newSponsor
    },

    async removeSponsorFromCourse(courseSlug, sponsorId) {
      const course = this.courses.find(c => c.slug === courseSlug)
      if (!course) throw new Error('Curso não encontrado.')
      if (!course.sponsors) course.sponsors = [...defaultSponsorsList]

      course.sponsors = course.sponsors.filter(s => s.id !== sponsorId)
      await updateCourseApi(course.slug, { sponsors: course.sponsors })
    },

    async createTurma(courseSlug, turmaPayload) {
      const course = this.courses.find(c => c.slug === courseSlug)
      if (!course) throw new Error('Curso não encontrado.')
      
      if (course.status === 'closed') {
        throw new Error('Não é possível criar turmas em cursos finalizados.')
      }

      const count = (course.turmas || []).length + 1
      const newTurma = {
        id: `turma-${courseSlug}-${Date.now().toString().slice(-4)}`,
        name: turmaPayload.name || `Turma ${count.toString().padStart(2, '0')}`,
        dateText: turmaPayload.dateText || course.dateText,
        cityState: turmaPayload.cityState || course.cityState,
        address: turmaPayload.address || course.address,
        scheduleHours: turmaPayload.scheduleHours || course.scheduleHours,
        maxSeats: parseInt(turmaPayload.maxSeats || 20),
        status: turmaPayload.status || 'open',
        students: []
      }

      if (!course.turmas) course.turmas = []
      course.turmas.push(newTurma)
      await updateCourseApi(course.slug, { turmas: course.turmas })
      return newTurma
    },

    async updateTurma(courseSlug, turmaId, updatedFields) {
      const course = this.courses.find(c => c.slug === courseSlug)
      if (!course) throw new Error('Curso não encontrado.')
      const turma = (course.turmas || []).find(t => t.id === turmaId)
      if (!turma) throw new Error('Turma não encontrada.')

      Object.assign(turma, updatedFields)
      await updateCourseApi(course.slug, { turmas: course.turmas })
      return turma
    },

    async registerDraftLead(courseSlug, turmaId, leadPayload) {
      const course = this.courses.find(c => c.slug === courseSlug)
      if (!course) throw new Error('Curso não encontrado.')
      const turma = (course.turmas || []).find(t => t.id === turmaId)
      if (!turma) throw new Error('Turma não encontrada.')
      if (!turma.students) turma.students = []

      let student = turma.students.find(s => s.email.toLowerCase() === leadPayload.email.toLowerCase())
      
      if (student) {
        student.name = leadPayload.name || student.name
        student.phone = leadPayload.phone || student.phone
        student.experience = leadPayload.experience || student.experience
        student.source = leadPayload.source || student.source
        student.stepReached = leadPayload.stepReached || student.stepReached || 1
      } else {
        student = {
          id: `lead-${Date.now().toString().slice(-6)}`,
          name: leadPayload.name,
          email: leadPayload.email,
          phone: leadPayload.phone || '',
          experience: leadPayload.experience || 'Iniciante do zero',
          source: leadPayload.source || 'Site',
          notes: leadPayload.notes || '',
          status: 'draft',
          stepReached: leadPayload.stepReached || 1,
          registeredAt: new Date().toISOString()
        }
        turma.students.unshift(student)
      }

      await updateCourseApi(course.slug, { turmas: course.turmas })
      return student
    },

    async addStudent(courseSlug, turmaId, studentPayload) {
      const course = this.courses.find(c => c.slug === courseSlug)
      if (!course) throw new Error('Curso não encontrado.')
      const turma = (course.turmas || []).find(t => t.id === turmaId)
      if (!turma) throw new Error('Turma não encontrada.')
      if (!turma.students) turma.students = []

      let student = turma.students.find(s => s.email.toLowerCase() === studentPayload.email.toLowerCase())
      
      if (student) {
        student.name = studentPayload.name
        student.phone = studentPayload.phone
        student.experience = studentPayload.experience
        student.source = studentPayload.source
        student.notes = studentPayload.notes
        student.status = studentPayload.status || 'waitlist'
        student.stepReached = 3
      } else {
        student = {
          id: `stu-${Date.now().toString().slice(-6)}`,
          name: studentPayload.name,
          email: studentPayload.email,
          phone: studentPayload.phone || '',
          experience: studentPayload.experience || 'Iniciante do zero',
          source: studentPayload.source || 'Site',
          notes: studentPayload.notes || '',
          status: studentPayload.status || 'waitlist',
          stepReached: 3,
          registeredAt: new Date().toISOString()
        }
        turma.students.unshift(student)
      }

      await updateCourseApi(course.slug, { turmas: course.turmas })
      return student
    },

    async updateStudentStatus(courseSlug, turmaId, studentId, newStatus) {
      const course = this.courses.find(c => c.slug === courseSlug)
      if (!course) throw new Error('Curso não encontrado.')
      const turma = (course.turmas || []).find(t => t.id === turmaId)
      if (!turma) throw new Error('Turma não encontrada.')
      const student = (turma.students || []).find(s => s.id === studentId)
      if (!student) throw new Error('Aluno não encontrado.')

      student.status = newStatus
      await updateCourseApi(course.slug, { turmas: course.turmas })
    },

    async removeStudent(courseSlug, turmaId, studentId) {
      const course = this.courses.find(c => c.slug === courseSlug)
      if (!course) throw new Error('Curso não encontrado.')
      const turma = (course.turmas || []).find(t => t.id === turmaId)
      if (!turma) throw new Error('Turma não encontrada.')

      turma.students = (turma.students || []).filter(s => s.id !== studentId)
      await updateCourseApi(course.slug, { turmas: course.turmas })
    },

    async deleteCourse(slug) {
      await deleteCourseApi(slug)
      await this.initStore()
    },

    triggerSavePage() {
      this.saveTriggerCount++
    },

    triggerSponsorModal() {
      this.sponsorModalTriggerCount++
    }
  }
})
